import type { CardImageLayer, CardTemplate } from './types';
import { line, text, type LocalizedContent } from './types';

/**
 * The party invitation ("Uredite sami" on the printed-set screen): an A5
 * invitation front with a QR slot in the middle.
 *
 * Authored in code, not in the template database, because its QR slot and its
 * name/date lines are filled per event: the design page builds it with the
 * event's styled QR (uploaded the way a customer photo is) and the event's
 * name and date. Without an event it is an ordinary invitation: the default
 * lines below and an empty photo slot the customer may fill or delete.
 */
export const PARTY_INVITATION_CODE = 'party-invitation';

/** The live invitation product's page: A5 portrait. */
const CANVAS = { widthMm: 148, heightMm: 210, bleedMm: 3, safeMm: 5 } as const;

const INTRO: LocalizedContent = {
  sl: 'Vabimo vas na',
  hr: 'Pozivamo vas na',
  sk: 'Pozývame vás na',
  cs: 'Zveme vás na',
  pl: 'Serdecznie zapraszamy na',
  en: 'You are warmly invited to',
  'de-AT': 'Herzliche Einladung zu',
  it: 'Siete invitati a',
};

const NAME_PLACEHOLDER: LocalizedContent = {
  sl: 'Ime dogodka',
  hr: 'Naziv događaja',
  sk: 'Názov podujatia',
  cs: 'Název akce',
  pl: 'Nazwa wydarzenia',
  en: 'Event name',
  'de-AT': 'Name der Feier',
  it: "Nome dell'evento",
};

const DATE_PLACEHOLDER: LocalizedContent = {
  sl: 'Datum in ura',
  hr: 'Datum i vrijeme',
  sk: 'Dátum a čas',
  cs: 'Datum a čas',
  pl: 'Data i godzina',
  en: 'Date and time',
  'de-AT': 'Datum und Uhrzeit',
  it: 'Data e ora',
};

const CAPTION: LocalizedContent = {
  sl: 'Skenirajte kodo in delite fotografije z nami',
  hr: 'Skenirajte kod i podijelite fotografije s nama',
  sk: 'Naskenujte kód a podeľte sa s nami o fotografie',
  cs: 'Naskenujte kód a sdílejte s námi fotografie',
  pl: 'Prosimy zeskanować kod i podzielić się z nami zdjęciami',
  en: 'Scan the code and share your photos with us',
  'de-AT': 'Scannen Sie den Code und teilen Sie Ihre Fotos mit uns',
  it: 'Scansioni il codice e condivida con noi le Sue foto',
};

const NAME: Record<string, string> = {
  sl: 'Vabilo s kodo QR',
  hr: 'Pozivnica s QR kodom',
  sk: 'Pozvánka s QR kódom',
  cs: 'Pozvánka s QR kódem',
  pl: 'Zaproszenie z kodem QR',
  en: 'Invitation with QR code',
  'de-AT': 'Einladung mit QR-Code',
  it: 'Invito con codice QR',
};

/** The QR slot's widest box; a taller QR (one with a frame text) keeps its aspect and fits the height. */
const QR_BOX = { centerX: CANVAS.widthMm / 2, top: 88, maxWidth: 64, maxHeight: 82 };

/** A square clip covering the whole box: the QR keeps its quiet zone, nothing is cropped. */
const SQUARE_FRAME = { paths: [{ d: 'M 0,0 H 100 V 100 H 0 Z' }], viewBox: { width: 100, height: 100 } };

export interface PartyInvitationFill {
  /** The event name; replaces the name placeholder. */
  name?: string;
  /** The event date, already formatted for the locale; replaces the date placeholder. */
  date?: string;
  /** The uploaded QR image: its storage key and height / width. */
  qr?: { sourceKey: string; aspect: number };
}

function qrLayer(qr: PartyInvitationFill['qr']): CardImageLayer {
  const aspect = qr && qr.aspect > 0 ? qr.aspect : 1;
  const width = Math.min(QR_BOX.maxWidth, QR_BOX.maxHeight / aspect);
  const height = width * aspect;
  return {
    type: 'image',
    sourceKey: qr?.sourceKey ?? '',
    frame: SQUARE_FRAME,
    z: 4,
    transform: { x: QR_BOX.centerX - width / 2, y: QR_BOX.top, width, height, rotation: 0 },
  };
}

export function buildPartyInvitationTemplate(fill: PartyInvitationFill = {}): CardTemplate {
  const ink = '#2F2A26';
  const accent = '#7A5C3E';
  return {
    id: PARTY_INVITATION_CODE,
    kind: 'invitation',
    categories: [],
    name: NAME,
    paperIds: [],
    showcasePaperId: null,
    slots: [
      {
        slotKey: 'front',
        canvas: { ...CANVAS },
        background: { color: '#FFFBF5' },
        layers: [
          line({ x: 54, y: 20, width: 40 }, 1, { stroke: accent, strokeWidth: 0.4 }),
          text(INTRO, 'Poppins', 12, accent, { x: 14, y: 26, width: 120, height: 9 }, 2),
          text(fill.name?.trim() || NAME_PLACEHOLDER, 'Playfair Display', 26, ink, { x: 10, y: 37, width: 128, height: 26 }, 3),
          text(fill.date?.trim() || DATE_PLACEHOLDER, 'Poppins', 11, ink, { x: 14, y: 66, width: 120, height: 9 }, 3),
          line({ x: 54, y: 80, width: 40 }, 1, { stroke: accent, strokeWidth: 0.4 }),
          qrLayer(fill.qr),
          text(CAPTION, 'Poppins', 10, accent, { x: 14, y: 176, width: 120, height: 14 }, 5),
        ],
      },
    ],
  };
}

/** "sobota, 14. november 2026 ob 18:00" in the page's locale; first letter upper case. Empty for a bad date. */
export function formatPartyInvitationDate(startsAt: string, locale: string): string {
  const at = new Date(startsAt);
  if (Number.isNaN(at.getTime())) return '';
  const tag = locale === 'si' ? 'sl' : locale;
  let s: string;
  try {
    s = at.toLocaleString(tag, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    s = at.toLocaleString();
  }
  return s.charAt(0).toUpperCase() + s.slice(1);
}
